import { AREA_UTIL, FOLHA, areaUtilEmPx, type Tamanho } from "@foto/shared";

/**
 * A conta do visor de imagens, sem DOM: o componente só liga estes números aos gestos da biblioteca.
 *
 * Tudo aqui parte de uma escolha: o conteúdo que dá zoom é a imagem **no tamanho dela** (1181 × 1748
 * px, no caso da folha), e a escala da biblioteca é, portanto, "pixels da tela por pixel do arquivo".
 * Assim "100%" é literalmente 1 pixel do arquivo para 1 pixel CSS — que é o que serve para conferir
 * um acento —, e o percentual mostrado é a própria escala vezes cem.
 */

/** Uma área da imagem em frações (0 a 1) dela, a partir do canto superior esquerdo. */
export interface AreaEmFracoes {
  x: number;
  y: number;
  largura: number;
  altura: number;
}

/** Um toque na tela: onde e quando. */
export interface Toque {
  x: number;
  y: number;
  /** Em milissegundos. */
  t: number;
}

const ehTamanhoValido = (t: Tamanho): boolean => t.largura > 0 && t.altura > 0;

/**
 * A escala que faz a imagem inteira caber na área do visor ("Ajustar").
 *
 * Arredonda para **baixo**, na quarta casa: arredondar para cima faria a imagem passar da área por
 * uma fração de pixel, e o visor passaria a deixar arrastar 0,1 px — com o cursor de mão aparecendo
 * numa imagem que está "ajustada". Não limita a 100%: numa tela grande, ajustar pode ampliar, e o
 * rótulo mostra o percentual real.
 */
export function escalaDeAjuste(conteudo: Tamanho, area: Tamanho): number {
  if (!ehTamanhoValido(conteudo) || !ehTamanhoValido(area)) return 1;
  const escala = Math.min(area.largura / conteudo.largura, area.altura / conteudo.altura);
  return Math.floor(escala * 10_000) / 10_000;
}

/**
 * A escala em que 1 pixel do arquivo ocupa 1 pixel CSS, dado o tamanho **natural** do arquivo e o
 * tamanho em que o conteúdo é desenhado. Quando são iguais (o caso da folha) vale 1; se o servidor
 * entregasse o dobro de pixels num conteúdo declarado com a metade, "100%" seria 2 — e continuaria
 * sendo um pixel do arquivo por pixel da tela.
 */
export function escalaDeCemPorCento(natural: Tamanho, renderizado: Tamanho): number {
  if (!ehTamanhoValido(natural) || !ehTamanhoValido(renderizado)) return 1;
  return natural.largura / renderizado.largura;
}

/** O zoom como o operador o lê: 100 = 1 pixel do arquivo por pixel da tela. */
export function percentualDoZoom(escala: number, escalaDeCem: number): number {
  return Math.round((escala / escalaDeCem) * 100);
}

export function rotuloDoZoom(escala: number, escalaDeCem: number): string {
  return `${percentualDoZoom(escala, escalaDeCem)}%`;
}

export interface LimitesDeZoom {
  minimo: number;
  maximo: number;
}

/**
 * Até onde o zoom vai. O mínimo é "Ajustar" — afastar mais só deixaria a imagem pequena no meio do
 * vazio —, mas nunca acima de 100%: se ajustar ampliasse (tela enorme), o 100% ficaria inalcançável.
 * O máximo é 400% do arquivo ou 6 vezes o ajustado, o que for maior, para que uma imagem pequena
 * numa tela grande ainda tenha folga para ampliar.
 *
 * O máximo vai em duas casas porque a biblioteca arredonda o alvo de um zoom por botão a duas casas:
 * um máximo com mais precisão nunca seria alcançado exatamente.
 */
export function limitesDeZoom(ajustada: number, escalaDeCem: number): LimitesDeZoom {
  const maximo = Math.max(4 * escalaDeCem, 6 * ajustada);
  return { minimo: Math.min(ajustada, escalaDeCem), maximo: Math.round(maximo * 100) / 100 };
}

/** Os degraus dos botões e das teclas, em múltiplos do arquivo (1 = 100%). */
const DEGRAUS = [0.25, 0.5, 0.75, 1, 1.5, 2, 3, 4, 6, 8] as const;

/** Duas escalas a menos de 2% uma da outra são o mesmo degrau para o operador. */
const PROXIMAS = 0.02;

/**
 * Os pontos de parada do zoom por botão: o mínimo, o "ajustar", o máximo e os degraus redondos que
 * ficam entre eles. Um degrau a menos de 2% de um dos três primeiros some — "50%" ao lado de um
 * "ajustar" de 49% seria um clique que não muda nada.
 */
function pontosDeParada(
  ajustada: number,
  escalaDeCem: number,
  { minimo, maximo }: LimitesDeZoom,
): number[] {
  const fixos = [minimo, Math.min(Math.max(ajustada, minimo), maximo), maximo];
  const degraus = DEGRAUS.map((d) => d * escalaDeCem).filter(
    (v) => v > minimo && v < maximo && fixos.every((f) => Math.abs(v / f - 1) > PROXIMAS),
  );
  return [...fixos, ...degraus]
    .sort((a, b) => a - b)
    .filter((v, i, todos) => i === 0 || v > todos[i - 1]! * 1.0001);
}

/**
 * Para onde vai o zoom num passo dos botões `+`/`−` ou das teclas: o ponto de parada seguinte na
 * direção pedida. Parte da escala **atual**, que pode estar entre dois pontos (a roda do mouse não
 * para em degrau), e fica no limite quando não há mais para onde ir.
 */
export function proximoPasso(
  escala: number,
  sentido: 1 | -1,
  ajustada: number,
  escalaDeCem: number,
): number {
  const limites = limitesDeZoom(ajustada, escalaDeCem);
  const pontos = pontosDeParada(ajustada, escalaDeCem, limites);
  if (sentido === 1) {
    return pontos.find((p) => p > escala * (1 + 0.005)) ?? limites.maximo;
  }
  return [...pontos].reverse().find((p) => p < escala / (1 + 0.005)) ?? limites.minimo;
}

/** A imagem está "ajustada" (em tolerância de meio por cento): é o que acende o botão e some com a mão. */
export function estaAjustada(escala: number, ajustada: number): boolean {
  return Math.abs(escala / ajustada - 1) < 0.005;
}

/**
 * O que o toque ou clique duplo faz: ajustada, vai a 100%; em qualquer outro zoom, volta a ajustar.
 * Se "ajustar" já for 100% (ou quase), o duplo clique não teria o que alternar — fica em ajustar.
 */
export function alvoDoToqueDuplo(
  escala: number,
  ajustada: number,
  escalaDeCem: number,
): "ajustar" | "cem" {
  const distinto = Math.abs(escalaDeCem / ajustada - 1) >= 0.01;
  return estaAjustada(escala, ajustada) && distinto ? "cem" : "ajustar";
}

/** Dois toques seguidos contam como duplo se vierem em até 300 ms e a menos de 32 px um do outro. */
export const JANELA_DO_TOQUE_DUPLO_MS = 300;
export const DISTANCIA_DO_TOQUE_DUPLO_PX = 32;

export function ehToqueDuplo(anterior: Toque | null, atual: Toque): boolean {
  if (!anterior) return false;
  const intervalo = atual.t - anterior.t;
  const distancia = Math.hypot(atual.x - anterior.x, atual.y - anterior.y);
  return (
    intervalo >= 0 &&
    intervalo <= JANELA_DO_TOQUE_DUPLO_MS &&
    distancia <= DISTANCIA_DO_TOQUE_DUPLO_PX
  );
}

/**
 * A "unidade" do passo da roda: quanto da escala atual um toque de roda vira.
 *
 * A biblioteca soma `passo × |deltaY|` à escala, e é o `passo` que precisamos acertar — fixo, ele
 * seria enorme a 38% e minúsculo a 400%. Proporcional à escala, cada giro amplia a mesma fração,
 * que é como o olho mede zoom. Uma volta de roda de mouse (deltaY ≈ 100) dá ~22%.
 *
 * `deltaMode` entra porque o Firefox entrega a roda em **linhas** (deltaY ≈ 3 por volta) e não em
 * pixels: sem converter, a mesma volta ampliaria um trigésimo. E a pinça do trackpad chega como roda
 * com `ctrlKey`, com deltas bem menores que os de uma volta — por isso o passo dela é maior.
 */
export function passoDaRoda(escala: number, deltaMode: number, pinca: boolean): number {
  const PASSO_POR_PIXEL = 0.0022;
  const unidade = deltaMode === 1 ? 100 / 3 : deltaMode === 2 ? 100 : 1;
  return escala * PASSO_POR_PIXEL * unidade * (pinca ? 4 : 1);
}

/** Quanto uma tecla de seta move a imagem, em pixels da tela. */
export const PASSO_DAS_SETAS_PX = 96;

/**
 * A área útil do papel (93,6 × 141 mm, no meio do postal) em frações da folha de 1181 × 1748 px —
 * a mesma conta de `areaUtilEmPx`, só que a coordenada que o visor desenha sobre a imagem é a que
 * acompanha o zoom.
 */
export function areaUtilDaFolha(): AreaEmFracoes {
  const area = areaUtilEmPx();
  return {
    x: area.x / FOLHA.larguraPx,
    y: area.y / FOLHA.alturaPx,
    largura: area.largura / FOLHA.larguraPx,
    altura: area.altura / FOLHA.alturaPx,
  };
}

/** `93.6` → `93,6`: a vírgula decimal que o operador lê. */
export const emMm = (mm: number): string => String(mm).replace(".", ",");

/** A legenda da linha da área útil — a mesma explicação que o editor de enquadramento dá. */
export function legendaDaAreaUtil(): string {
  return `A linha azul é a área que o papel imprime (${emMm(AREA_UTIL.larguraMm)} × ${emMm(AREA_UTIL.alturaMm)} mm); o que fica fora cai no picotado.`;
}

/** O arquivo da folha, em pixels: o que o visor mede quando abre a folha de marcadores ou o JPEG da impressão. */
export const TAMANHO_DA_FOLHA: Tamanho = { largura: FOLHA.larguraPx, altura: FOLHA.alturaPx };

/** A área útil da folha em frações dela. Constante de módulo: o visor compara por identidade. */
export const AREA_UTIL_DA_FOLHA: AreaEmFracoes = areaUtilDaFolha();

/**
 * Uma das imagens que o visor alterna entre si: "Peça com capa" e "Foto da IA", por exemplo.
 *
 * Sem `tamanho`, o visor mede a imagem — o servidor não manda as dimensões das fotos. Com ele, a
 * conta começa no primeiro quadro e `previa` já aparece por baixo.
 */
export interface VersaoDoVisor {
  id: string;
  /** O que aparece na chave de versões. */
  rotulo: string;
  /** A imagem em resolução cheia — a que o "100%" mede. */
  url: string;
  /** A versão leve, que já está na página. */
  previa?: string | undefined;
  tamanho?: Tamanho | undefined;
}

/**
 * A versão com que o visor abre: a pedida, se a lista a tem; senão, a primeira. Quem abre o visor
 * de uma foto passa a versão que estava à vista na grade, e uma foto sem a versão "Foto da IA" não
 * pode abrir numa que não existe.
 */
export function versaoInicialDoVisor(
  versoes: readonly { id: string }[],
  desejada?: string,
): string {
  if (desejada !== undefined && versoes.some((v) => v.id === desejada)) return desejada;
  return versoes[0]?.id ?? "";
}

/**
 * Duas imagens têm a mesma proporção (em um por cento): é o que decide se o zoom e a posição
 * sobrevivem à troca de versão. A peça e a foto da IA costumam ter proporções diferentes (9 × 16 e
 * quadrada); duas fotos sem capa, não.
 */
export function mesmaProporcao(a: Tamanho, b: Tamanho): boolean {
  if (!ehTamanhoValido(a) || !ehTamanhoValido(b)) return false;
  return Math.abs(a.largura / a.altura / (b.largura / b.altura) - 1) < 0.01;
}

/** Onde a imagem está na tela: a escala da biblioteca e o canto superior esquerdo, em pixels da tela. */
export interface VistaDoZoom {
  escala: number;
  x: number;
  y: number;
}

/** De onde a nova versão parte: a vista, e se é para centralizar (ajustada) ou manter a posição. */
export interface PartidaDoZoom extends VistaDoZoom {
  centrar: boolean;
}

/**
 * A vista de uma versão que o operador deixou, para a troca: o que era a imagem antes, e como ela
 * estava na tela.
 */
export interface VistaAnterior {
  tamanho: Tamanho;
  /** A escala de "Ajustar" daquela versão. */
  ajustada: number;
  vista: VistaDoZoom;
}

/**
 * Com que zoom e em que posição a nova versão aparece.
 *
 * Mesma proporção: a mesma parte da imagem continua no mesmo lugar da tela. A escala da biblioteca
 * é "pixels da tela por pixel do arquivo", então, se o arquivo novo tem outra largura em pixels, a
 * escala muda na razão inversa — o que se vê na tela é o mesmo — e a posição não muda. Quem
 * estava ajustado continua ajustado.
 *
 * Proporção diferente: a mesma região não existe na outra imagem. Reenquadra em "Ajustar".
 */
export function partidaAoTrocarDeVersao(
  anterior: VistaAnterior | null,
  novo: Tamanho,
  ajustadaDoNovo: number,
): PartidaDoZoom {
  const ajustar: PartidaDoZoom = { escala: ajustadaDoNovo, x: 0, y: 0, centrar: true };
  if (!anterior || !mesmaProporcao(anterior.tamanho, novo)) return ajustar;
  if (estaAjustada(anterior.vista.escala, anterior.ajustada)) return ajustar;
  return {
    escala: anterior.vista.escala * (anterior.tamanho.largura / novo.largura),
    x: anterior.vista.x,
    y: anterior.vista.y,
    centrar: false,
  };
}
